import { isFakeMousedownFromScreenReader } from '@angular/cdk/a11y';
import {
  BooleanInput,
  coerceBooleanProperty,
  coerceElement,
} from '@angular/cdk/coercion';
import {
  AfterViewInit,
  Directive,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
} from '@angular/core';

import { RippleState } from './enums/ripple-directive.enums';
import { Ripple } from './models/ripple.model';

// TODO: test this thing

@Directive({ standalone: true, selector: '[awRipple]' })
export class RippleDirective
  implements EventListenerObject, AfterViewInit, OnDestroy
{
  @Input()
  set rippleDisabled(_disabled: BooleanInput) {
    this._disabled = coerceBooleanProperty(_disabled);
    this.onDisabledChange(this._disabled);
  }

  get rippleDisabled(): boolean {
    return this._disabled;
  }

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly ngZone: NgZone,
  ) {
    this.trigger = coerceElement(this.elementRef);
  }

  private _disabled = false;

  /** Time in milliseconds when the last touchstart event happened. */
  private lastTouchStartEvent?: number;

  private classesAddedToContainer: Array<string> = [];

  private eventsRegistered = false;

  private isPointerDown = false;

  private activeRipples: Array<Ripple> = [];

  private readonly trigger: HTMLElement;

  private readonly enterDuration = 500;

  private readonly exitDuration = 500;

  /** Events that signal that the pointer is down. */
  private readonly pointerDownEvents = ['mousedown', 'touchstart'];

  /** Events that signal that the pointer is up. */
  private readonly pointerUpEvents = [
    'mouseup',
    'mouseleave',
    'touchend',
    'touchcancel',
  ];

  /**
   * Timeout for ignoring mouse events. Mouse events will be temporary ignored after touch
   * events to avoid synthetic mouse events.
   */
  private readonly ignoreMouseEventsTimeout = 800;

  private readonly eventListenerOptions: AddEventListenerOptions = {
    passive: true,
  };

  ngAfterViewInit(): void {
    if (!this.rippleDisabled) {
      this.registerEvents();
    }
  }

  handleEvent(event: Event): void {
    if (this.pointerDownEvents.includes(event.type)) {
      this.onMouseDown(event as MouseEvent);
    } else if (this.isPointerDown) {
      this.isPointerDown = false;

      this.onMouseUp();
    }
  }

  ngOnDestroy(): void {
    this.removeEvents();
    this.destroyActiveRipples();
  }

  private registerEvents(): void {
    const allEvents = this.pointerDownEvents.concat(this.pointerUpEvents);

    this.ngZone.runOutsideAngular(() =>
      allEvents.forEach((event) => {
        this.trigger.addEventListener(event, this, this.eventListenerOptions);
      }),
    );

    this.eventsRegistered = true;
  }

  private removeEvents(): void {
    this.pointerDownEvents.forEach((event) =>
      this.trigger.removeEventListener(event, this, this.eventListenerOptions),
    );

    this.eventsRegistered = false;
  }

  private onDisabledChange(disabled: boolean): void {
    if (disabled && this.eventsRegistered) {
      this.removeEvents();
    } else if (!disabled && !this.eventsRegistered) {
      this.registerEvents();
    }
  }

  private onMouseDown(event: MouseEvent): void {
    const isFake = isFakeMousedownFromScreenReader(event);
    const isSynthetic = this.isSyntheticEvent();

    if (!isFake && !isSynthetic) {
      this.isPointerDown = true;

      this.ripple(event.clientX, event.clientY);
    }
  }

  private onMouseUp(): void {
    this.activeRipples.forEach((ripple) => {
      this.fadeOutRiple(ripple);
    });
  }

  private isSyntheticEvent(): boolean {
    return !!(
      this.lastTouchStartEvent &&
      Date.now() < this.lastTouchStartEvent + this.ignoreMouseEventsTimeout
    );
  }

  private ripple(x: number, y: number): void {
    const triggerRect = this.trigger.getBoundingClientRect();

    const radius = this.computeDistanceToFurthestCorner(x, y, triggerRect);
    const offsetX = x - triggerRect.left;
    const offsetY = y - triggerRect.top;

    const ripple = new Ripple(offsetX, offsetY, radius, this.enterDuration);

    this.classesAddedToContainer = this.addContainerClasses([
      'overflow-hidden',
      'relative',
    ]);
    this.trigger.appendChild(ripple.element);
    this.initRippleTransitionHandlers(ripple);
    this.activeRipples.push(ripple);

    /**
     * This line is CRITICAL and will break the entire ripple if removed.
     * This happens because browsers dont calculate styles of dynamically created elements
     * and thus the scale3d transform won't happen
     * To force this to happen, getComputedStyle first has to be called then a property has to be
     * accessed off the returned value
     * https://gist.github.com/paulirish/5d52fb081b3570c81e3a
     */
    window.getComputedStyle(ripple.element).transitionProperty;

    // Trigger the ripple animation
    ripple.element.style.transform = 'scale3d(1, 1, 1)';
  }

  private fadeOutRiple(ripple: Ripple): void {
    ripple.element.style.transitionDuration = `${this.exitDuration}ms`;
    ripple.element.style.opacity = '0';
    ripple.state = RippleState.FadingOut;
  }

  private destroyRipple(ripple: Ripple): void {
    this.activeRipples = this.activeRipples.filter(
      ({ id }) => id !== ripple.id,
    );

    ripple.element.remove();

    if (!this.activeRipples.length) {
      this.removeContainerClasses(this.classesAddedToContainer);
    }
  }

  private destroyActiveRipples(): void {
    this.activeRipples.forEach((ripple) => this.destroyRipple(ripple));
  }

  /**
   * Returns the distance from the point (x, y) to the furthest corner of a rectangle.
   */
  private readonly computeDistanceToFurthestCorner = (
    x: number,
    y: number,
    rect: DOMRect,
  ) => {
    const distX = Math.max(Math.abs(x - rect.left), Math.abs(x - rect.right));
    const distY = Math.max(Math.abs(y - rect.top), Math.abs(y - rect.bottom));

    return Math.sqrt(distX * distX + distY * distY);
  };

  /**
   * Adds the passed in classes to the container, if it doesn't already have them
   * @param classesToAdd the classes to add
   * @return the classes that were added
   */

  private addContainerClasses(classesToAdd: Array<string>): Array<string> {
    const addedClasses: Array<string> = [];

    classesToAdd.forEach((className) => {
      if (!this.trigger.classList.contains(className)) {
        addedClasses.push(className);
        this.trigger.classList.add(className);
      }
    });

    return addedClasses;
  }

  private removeContainerClasses(classesToRemove: Array<string>): void {
    classesToRemove.forEach((className) =>
      this.trigger.classList.remove(className),
    );
  }

  private initRippleTransitionHandlers(ripple: Ripple): void {
    this.ngZone.runOutsideAngular(() => {
      const removeListeners = () => {
        ripple.element.removeEventListener(
          'transitionend',
          transitionEndListener,
        );
        ripple.element.removeEventListener(
          'transitioncancel',
          transitionCancelListener,
        );
      };

      const transitionEndListener = () => {
        if (ripple.state === RippleState.FadingOut) {
          this.destroyRipple(ripple);
          removeListeners();
        }
      };

      const transitionCancelListener = () => {
        this.destroyRipple(ripple);
        removeListeners();
      };

      ripple.element.addEventListener('transitionend', transitionEndListener);
      ripple.element.addEventListener(
        'transitioncancel',
        transitionCancelListener,
      );
    });
  }
}
